import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Info, Zap, Hash, Layers, PlayCircle, Newspaper, Loader2 } from 'lucide-react';
import BrandCard from '../../components/features/BrandCard';
import QABlock from '../../components/features/QABlock';
import InsightBlock from '../../components/features/InsightBlock';
import { getPublicTopic, getPublicTopicQA } from '../../lib/apiClient';

export default function TopicHubPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [qaData, setQaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
        try {
            setLoading(true);
            const [topicRes, qaRes] = await Promise.all([
              getPublicTopic(slug),
              getPublicTopicQA(slug).catch(() => [])
            ]);
            setData(topicRes);
            const qaList = Array.isArray(qaRes?.data)
              ? qaRes.data
              : (Array.isArray(qaRes) ? qaRes : []);
            setQaData(qaList);
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setLoading(false);
        }
    }
    load();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-white"><Loader2 className="w-8 h-8 animate-spin text-primary"/></div>;
  if (error || !data) return <div className="min-h-screen bg-background flex items-center justify-center text-white">Topic not found</div>;

  const { topic, insights, contributing_brands } = data;

  // Helper to format brands for the card
  const formattedBrands = contributing_brands.map(b => ({
      name: b.projects.name,
      slug: b.projects.slug,
      insights: b.insight_count,
      descriptor: b.projects.description || 'Leading the conversation.',
      website_url: b.projects.website_url || null,
      logo_url: b.projects.logo_url || null,
  }));

  // Generate FAQPage JSON-LD schema
  const faqSchema = qaData.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": qaData.map(qa => ({
      "@type": "Question",
      "name": qa.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": qa.answer
      }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-background text-white font-sans pb-20">
      {/* FAQ Schema */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {/* Breadcrumbs */}
      <div className="container mx-auto max-w-6xl px-6 pt-8 pb-4 text-sm text-slate-500">
        <Link to="/" className="hover:text-white transition-colors">Home</Link> &gt; <Link to="/topics" className="hover:text-white transition-colors">Topics</Link> &gt; <span className="text-white">{topic.title}</span>
      </div>

      {/* Topic Header */}
      <div className="container mx-auto max-w-6xl px-6 mb-12">
        <div className="bg-surface/50 border border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden">
          {/* Glow effect */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none" />
          
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight text-white">{topic.title}</h1>
              
              {/* Hero Summary - AI-generated overview based on insights */}
              {topic.hero_summary && (
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">{topic.hero_summary}</p>
              )}
              
              {/* Fallback to description if no hero summary */}
              {!topic.hero_summary && topic.description && (
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">{topic.description}</p>
              )}
              
              <div className="flex flex-wrap gap-2">
                 {/* Keywords if available, else just a placeholder tag */}
                 {topic.keywords && topic.keywords.map(k => (
                     <span key={k} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-slate-400">{k}</span>
                 ))}
              </div>
            </div>

            {/* Explanatory Panel */}
            <div className="bg-black/20 rounded-xl p-6 border border-white/5 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-4 text-primary">
                <Info className="w-5 h-5" />
                <h3 className="font-bold text-sm uppercase tracking-wide">About This Topic</h3>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  {topic.description || 'Explore insights and perspectives from industry leaders.'}
              </p>
              <div>
                <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Coverage Stats</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <div className="flex justify-between">
                    <span>Insights:</span>
                    <span className="font-bold text-white">{insights?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Contributing Brands:</span>
                    <span className="font-bold text-white">{contributing_brands?.length || 0}</span>
                  </div>
                  {topic.hero_summary_generated_at && (
                    <div className="pt-2 mt-2 border-t border-white/5">
                      <span className="text-xs text-slate-500">
                        Summary updated {new Date(topic.hero_summary_generated_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 grid lg:grid-cols-3 gap-12">
        
        {/* Left Column: Insights & Content */}
        <div className="lg:col-span-2 space-y-12">
          
          {/* Q&A Section */}
          {qaData.length > 0 && (
            <QABlock qaList={qaData.slice(0, 5)} topicSlug={slug} />
          )}

          {/* Key Insights - Collapsible Cards */}
          {insights.length > 0 ? (
            <InsightBlock insights={insights} topicSlug={topic.slug} />
          ) : (
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Zap className="w-6 h-6 text-yellow-500" />
                  Key Insights
                </h2>
              </div>
              <div className="text-slate-500 italic">No insights yet. Be the first to contribute.</div>
            </section>
          )}


          {/* In The News (Resurfacing) */}
          <section>
            <div className="flex items-center gap-2 mb-6">
              <Newspaper className="w-5 h-5 text-secondary" />
              <h2 className="text-xl font-bold">In the News</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {newsItems.map((news, i) => (
                <div key={i} className="p-5 rounded-xl bg-surface/30 border border-white/5 hover:border-secondary/30 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-white leading-tight">{news.headline}</h4>
                    <span className="text-xs text-slate-500 whitespace-nowrap ml-2">{news.date}</span>
                  </div>
                  <p className="text-sm text-slate-400 mb-3">{news.summary}</p>
                  <button className="text-xs font-bold text-secondary hover:text-white transition-colors flex items-center gap-1">
                    View resurfaced insight <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </section>

        </div>

        {/* Right Column: Brands & Related */}
        <div className="space-y-12">
          
          {/* Contributing Brands */}
          <section>
            <div className="flex items-center gap-2 mb-6">
              <Layers className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold">Contributing Brands</h2>
            </div>
            <div className="space-y-4">
              {formattedBrands.map((brand, i) => (
                <BrandCard key={i} brand={brand} />
              ))}
              {formattedBrands.length === 0 && <div className="text-slate-500">No brands yet.</div>}
            </div>
          </section>

          {/* Related Topics - TODO: Fetch from API */}
          {/* 
          <section>
            <div className="flex items-center gap-2 mb-6">
              <Hash className="w-5 h-5 text-slate-400" />
              <h2 className="text-xl font-bold">Related Topics</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {relatedTopics.map((topic, i) => (
                <Link key={i} to={`/topics/${topic.slug}`} className="px-3 py-2 rounded-lg bg-surface border border-white/5 text-sm text-slate-300 hover:text-white hover:border-primary/30 hover:bg-primary/5 transition-all">
                  {topic.title}
                </Link>
              ))}
            </div>
          </section>
          */}

          {/* Sticky Pricing Teaser */}
          <div className="sticky top-24 p-6 rounded-2xl bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-white/10 text-center">
            <h3 className="font-bold text-white mb-2">Own this topic?</h3>
            <p className="text-sm text-slate-400 mb-4">
              Turn your podcast into the authority source for <strong>{topic.title}</strong>.
            </p>
            <Link to="/pricing" className="block w-full py-2 bg-white text-black font-bold rounded-lg hover:bg-slate-200 transition-colors text-sm">
              Explore Plans
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}

// Placeholder for news (mock for now)
const newsItems = [
  { headline: 'Industry Shift Detected', summary: 'Recent episodes indicate a strong pivot towards this topic.', date: 'This week' },
];
